import { useState, useEffect, useCallback } from 'react';
import Navigation from '../components/Navigation';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { 
  Sparkles, Heart, Droplet, Activity, Brain, Moon, Shield,
  TrendingUp, TrendingDown, AlertTriangle, CheckCircle, Crown,
  Loader2, RefreshCw, ChevronRight, Target, Clock, Zap,
  PieChart, BarChart3, Calendar, Lightbulb, ArrowRight, Lock,
  Bot, MessageSquare, History
} from 'lucide-react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';

const RISK_COLORS = {
  low: "bg-green-500/20 text-green-400 border-green-500/30",
  moderate: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  high: "bg-orange-500/20 text-orange-400 border-orange-500/30",
  critical: "bg-red-500/20 text-red-400 border-red-500/30"
};

const RISK_ICONS = {
  cardiovascular: Heart,
  metabolic: Droplet,
  respiratory: Activity,
  mental_health: Brain,
  musculoskeletal: Target,
  immunity: Shield
};

const SUBSCRIPTION_TIERS = {
  free: { color: "bg-gray-500", name: "Free", icon: Lock },
  basic: { color: "bg-blue-500", name: "Basic", icon: Zap },
  premium: { color: "bg-purple-500", name: "Premium", icon: Crown },
  family: { color: "bg-emerald-500", name: "Family", icon: Heart }
};

const PremiumHealthPredictions = ({ user, setUser }) => {
  const [activeTab, setActiveTab] = useState('predictions');
  const [predictions, setPredictions] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [profile, setProfile] = useState(null);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [analyzingWithAI, setAnalyzingWithAI] = useState(false);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [selectedPrediction, setSelectedPrediction] = useState(null);

  const loadInitialData = useCallback(async () => {
    setLoading(true);
    try {
      const [subRes, profileRes] = await Promise.all([
        axios.get(`${API}/health-predictions/subscription/status`),
        axios.get(`${API}/health-predictions/profile`)
      ]);
      
      setSubscription(subRes.data);
      setProfile(profileRes.data.profile);
      
      // Check if predictions need to be generated
      if (subRes.data.tier !== 'free' || subRes.data.predictions_remaining > 0) {
        // Try to load from history first
        try {
          const historyRes = await axios.get(`${API}/health-predictions/history`);
          if (historyRes.data.history && historyRes.data.history.length > 0) {
            const latestId = historyRes.data.history[0].id;
            const detailRes = await axios.get(`${API}/health-predictions/prediction/${latestId}`);
            setPredictions(detailRes.data);
          }
        } catch (e) {
          process.env.NODE_ENV === 'development' && console.log('No previous predictions');
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
      // Try to create mock data if needed
      try {
        await axios.post(`${API}/health-predictions/mock-data`);
        const subRes = await axios.get(`${API}/health-predictions/subscription/status`);
        setSubscription(subRes.data);
        toast.success('Demo data created! You now have Premium access.');
      } catch (e) {
        console.error('Error creating mock data:', e);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  const generatePredictions = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/health-predictions/generate`, profile);
      setPredictions(response.data);
      setSubscription(prev => ({
        ...prev,
        predictions_remaining: response.data.subscription?.predictions_remaining
      }));
      toast.success('Health predictions generated successfully!');
      setActiveTab('predictions');
    } catch (error) {
      if (error.response?.status === 403) {
        setShowUpgradeDialog(true);
        toast.error('Prediction limit reached. Upgrade for more!');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to generate predictions');
      }
    } finally {
      setGenerating(false);
    }
  };

  const getAIAnalysis = async () => {
    if (!predictions?.predictions) {
      toast.error('Generate predictions first');
      return;
    }
    
    setAnalyzingWithAI(true);
    try {
      const response = await axios.post(`${API}/health-predictions/ai-analysis`, {
        predictions: predictions.predictions,
        profile: profile,
        focus_areas: predictions.summary?.areas_of_concern
      });
      setAiAnalysis(response.data);
      toast.success('AI analysis complete!');
      setActiveTab('ai-insights');
    } catch (error) {
      if (error.response?.status === 403) {
        setShowUpgradeDialog(true);
        toast.error('AI analysis requires Basic subscription or higher');
      } else {
        toast.error(error.response?.data?.detail || 'AI analysis failed');
      }
    } finally {
      setAnalyzingWithAI(false);
    }
  };

  const upgradeSubscription = async (tier) => {
    try {
      await axios.post(`${API}/health-predictions/subscription/upgrade`, { tier });
      const subRes = await axios.get(`${API}/health-predictions/subscription/status`);
      setSubscription(subRes.data);
      setShowUpgradeDialog(false);
      toast.success(`Upgraded to ${tier} tier!`);
    } catch (error) {
      toast.error('Upgrade failed');
    }
  };

  const updateProfile = async (newProfile) => {
    try {
      await axios.post(`${API}/health-predictions/profile/update`, newProfile);
      setProfile(newProfile);
      setShowProfileDialog(false);
      toast.success('Profile updated!');
    } catch (error) {
      toast.error('Failed to update profile');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
        <Navigation user={user} setUser={setUser} />
        <div className="container mx-auto px-4 py-8 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-purple-400 mx-auto" />
            <p className="mt-4 text-purple-200">Loading health predictions...</p>
          </div>
        </div>
      </div>
    );
  }

  const TierIcon = SUBSCRIPTION_TIERS[subscription?.tier || 'free']?.icon || Lock;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <Navigation user={user} setUser={setUser} />
      
      <div className="container mx-auto px-4 py-6 max-w-7xl">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="p-3 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 shadow-lg">
                  <Sparkles className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-white">
                    Premium Health Predictions
                  </h1>
                  <p className="text-purple-200 text-sm">
                    AI-powered 6-month health forecasting
                  </p>
                </div>
              </div>
            </div>
            
            {/* Subscription Badge */}
            <div className="flex items-center gap-3">
              <Badge className={`${SUBSCRIPTION_TIERS[subscription?.tier || 'free'].color} text-white px-4 py-2 text-sm flex items-center gap-2`}>
                <TierIcon className="w-4 h-4" />
                {SUBSCRIPTION_TIERS[subscription?.tier || 'free'].name} Plan
              </Badge>
              {subscription?.tier === 'free' && (
                <Button 
                  onClick={() => setShowUpgradeDialog(true)}
                  className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                  data-testid="upgrade-btn"
                >
                  <Crown className="w-4 h-4 mr-2" />
                  Upgrade
                </Button>
              )}
            </div>
          </div>

          {/* Quick Stats */}
          {predictions && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
              <Card className="bg-white/10 border-white/20 backdrop-blur-sm">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${predictions.health_grade === 'A' ? 'bg-green-500/20' : predictions.health_grade === 'B' ? 'bg-blue-500/20' : 'bg-yellow-500/20'}`}>
                    <Target className={`w-5 h-5 ${predictions.health_grade === 'A' ? 'text-green-400' : predictions.health_grade === 'B' ? 'text-blue-400' : 'text-yellow-400'}`} />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Health Score</p>
                    <p className="text-xl font-bold text-white">{predictions.health_score}</p>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-white/10 border-white/20 backdrop-blur-sm">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-purple-500/20">
                    <BarChart3 className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Grade</p>
                    <p className="text-xl font-bold text-white">{predictions.health_grade}</p>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-white/10 border-white/20 backdrop-blur-sm">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-red-500/20">
                    <AlertTriangle className="w-5 h-5 text-red-400" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">High Risk</p>
                    <p className="text-xl font-bold text-white">{predictions.summary?.high_risk_count || 0}</p>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-white/10 border-white/20 backdrop-blur-sm">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-green-500/20">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Strengths</p>
                    <p className="text-xl font-bold text-white">{predictions.summary?.strengths?.length || 0}</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </div>

        {/* Main Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white/10 border border-white/20 p-1 flex-wrap h-auto">
            <TabsTrigger value="predictions" className="data-[state=active]:bg-purple-500 data-[state=active]:text-white text-gray-300">
              <BarChart3 className="w-4 h-4 mr-2" />
              Predictions
            </TabsTrigger>
            <TabsTrigger value="ai-insights" className="data-[state=active]:bg-purple-500 data-[state=active]:text-white text-gray-300">
              <Bot className="w-4 h-4 mr-2" />
              AI Insights
            </TabsTrigger>
            <TabsTrigger value="profile" className="data-[state=active]:bg-purple-500 data-[state=active]:text-white text-gray-300">
              <Activity className="w-4 h-4 mr-2" />
              My Profile
            </TabsTrigger>
            <TabsTrigger value="history" className="data-[state=active]:bg-purple-500 data-[state=active]:text-white text-gray-300">
              <History className="w-4 h-4 mr-2" />
              History
            </TabsTrigger>
          </TabsList>

          {/* Predictions Tab */}
          <TabsContent value="predictions" className="space-y-6">
            {!predictions ? (
              <Card className="bg-white/10 border-white/20 backdrop-blur-sm">
                <CardContent className="p-8 text-center">
                  <Sparkles className="w-16 h-16 text-purple-400 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-white mb-2">Generate Your Health Predictions</h3>
                  <p className="text-gray-300 mb-6 max-w-md mx-auto">
                    Get AI-powered health risk predictions for the next 6 months based on your health profile.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Button 
                      onClick={() => setShowProfileDialog(true)}
                      variant="outline"
                      className="border-purple-500 text-purple-400 hover:bg-purple-500/20"
                      data-testid="update-profile-btn"
                    >
                      <Activity className="w-4 h-4 mr-2" />
                      Update Profile First
                    </Button>
                    <Button 
                      onClick={generatePredictions}
                      disabled={generating}
                      className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                      data-testid="generate-predictions-btn"
                    >
                      {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                      Generate Predictions
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Actions Bar */}
                <div className="flex flex-wrap gap-3 justify-between items-center">
                  <div className="flex gap-3">
                    <Button 
                      onClick={generatePredictions}
                      disabled={generating}
                      variant="outline"
                      className="border-purple-500 text-purple-400 hover:bg-purple-500/20"
                      data-testid="refresh-predictions-btn"
                    >
                      {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                      Regenerate
                    </Button>
                    <Button 
                      onClick={getAIAnalysis}
                      disabled={analyzingWithAI}
                      className="bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600"
                      data-testid="ai-analysis-btn"
                    >
                      {analyzingWithAI ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Bot className="w-4 h-4 mr-2" />}
                      Get AI Analysis
                    </Button>
                  </div>
                  <p className="text-xs text-gray-400">
                    Generated: {new Date(predictions.generated_at).toLocaleString()}
                  </p>
                </div>

                {/* Predictions Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {predictions.predictions?.map((pred, idx) => {
                    const Icon = RISK_ICONS[pred.category] || Activity;
                    return (
                      <Card 
                        key={`item-${idx}`} 
                        className="bg-white/10 border-white/20 backdrop-blur-sm hover:bg-white/15 transition-all cursor-pointer"
                        onClick={() => setSelectedPrediction(pred)}
                        data-testid={`prediction-card-${pred.prediction_type}`}
                      >
                        <CardHeader className="pb-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div className={`p-2 rounded-lg ${RISK_COLORS[pred.risk_level].split(' ')[0]}`}>
                                <Icon className={`w-5 h-5 ${RISK_COLORS[pred.risk_level].split(' ')[1]}`} />
                              </div>
                              <CardTitle className="text-white text-sm">{pred.name}</CardTitle>
                            </div>
                            <Badge className={`${RISK_COLORS[pred.risk_level]} text-xs`}>
                              {pred.risk_level}
                            </Badge>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-3">
                            <div>
                              <div className="flex justify-between text-xs mb-1">
                                <span className="text-gray-400">Risk Level</span>
                                <span className="text-white font-medium">{pred.current_risk}%</span>
                              </div>
                              <Progress 
                                value={pred.current_risk} 
                                className="h-2 bg-white/10"
                              />
                            </div>
                            
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-gray-400">Confidence</span>
                              <span className="text-purple-400">{pred.confidence_score}%</span>
                            </div>
                            
                            {pred.potential_improvement && (
                              <div className="flex items-center gap-2 text-xs text-green-400">
                                <TrendingDown className="w-3 h-3" />
                                <span>Can reduce by {pred.potential_improvement.reduction_possible}%</span>
                              </div>
                            )}
                            
                            <Button variant="ghost" size="sm" className="w-full text-purple-400 hover:text-purple-300 hover:bg-purple-500/20">
                              View Details <ChevronRight className="w-4 h-4 ml-1" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </>
            )}
          </TabsContent>

          {/* AI Insights Tab */}
          <TabsContent value="ai-insights" className="space-y-6">
            {!aiAnalysis ? (
              <Card className="bg-white/10 border-white/20 backdrop-blur-sm">
                <CardContent className="p-8 text-center">
                  <Bot className="w-16 h-16 text-blue-400 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-white mb-2">AI-Powered Health Analysis</h3>
                  <p className="text-gray-300 mb-6 max-w-md mx-auto">
                    Get personalized insights and recommendations from our AI health analyst.
                    {subscription?.tier === 'free' && ' Requires Basic subscription or higher.'}
                  </p>
                  <Button 
                    onClick={predictions ? getAIAnalysis : generatePredictions}
                    disabled={analyzingWithAI || generating}
                    className="bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600"
                    data-testid="get-ai-analysis-btn"
                  >
                    {analyzingWithAI ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Bot className="w-4 h-4 mr-2" />}
                    {predictions ? 'Analyze with AI' : 'Generate Predictions First'}
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6">
                {/* Executive Summary */}
                <Card className="bg-gradient-to-br from-blue-500/20 to-purple-500/20 border-white/20 backdrop-blur-sm">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Lightbulb className="w-5 h-5 text-yellow-400" />
                      Executive Summary
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-200 leading-relaxed">
                      {aiAnalysis.ai_analysis?.executive_summary}
                    </p>
                  </CardContent>
                </Card>

                {/* Risk Priorities */}
                {aiAnalysis.ai_analysis?.risk_priority?.length > 0 && (
                  <Card className="bg-white/10 border-white/20 backdrop-blur-sm">
                    <CardHeader>
                      <CardTitle className="text-white flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-orange-400" />
                        Priority Risk Areas
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {aiAnalysis.ai_analysis.risk_priority.map((riskItem, idx) => {
                        // Handle different formats: string, {area, reason}, or {risk, reason}
                        const riskText = typeof riskItem === 'string' 
                          ? riskItem 
                          : (riskItem.area || riskItem.risk || riskItem.name || JSON.stringify(riskItem));
                        const reasonText = typeof riskItem === 'object' ? riskItem.reason : null;
                        
                        return (
                          <div key={`item-${idx}`} className="flex items-start gap-3 p-3 bg-white/5 rounded-lg">
                            <div className="w-8 h-8 rounded-full bg-orange-500/20 flex items-center justify-center flex-shrink-0">
                              <span className="text-orange-400 font-bold">{idx + 1}</span>
                            </div>
                            <div>
                              <p className="text-white font-medium">{riskText}</p>
                              {reasonText && <p className="text-gray-400 text-sm mt-1">{reasonText}</p>}
                            </div>
                          </div>
                        );
                      })}
                    </CardContent>
                  </Card>
                )}

                {/* Action Plan */}
                {aiAnalysis.ai_analysis?.action_plan && (
                  <Card className="bg-white/10 border-white/20 backdrop-blur-sm">
                    <CardHeader>
                      <CardTitle className="text-white flex items-center gap-2">
                        <Calendar className="w-5 h-5 text-green-400" />
                        6-Month Action Plan
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {typeof aiAnalysis.ai_analysis.action_plan === 'string' ? (
                        <p className="text-gray-200 whitespace-pre-line">{aiAnalysis.ai_analysis.action_plan}</p>
                      ) : Array.isArray(aiAnalysis.ai_analysis.action_plan) ? (
                        <ul className="space-y-2">
                          {aiAnalysis.ai_analysis.action_plan.map((action, i) => (
                            <li key={`item-${i}`} className="text-gray-300 flex items-start gap-2">
                              <ArrowRight className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                              {typeof action === 'string' ? action : (action.action || action.step || action.description || JSON.stringify(action))}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <div className="space-y-4">
                          {Object.entries(aiAnalysis.ai_analysis.action_plan).map(([month, actions], idx) => (
                            <div key={`item-${idx}`} className="border-l-2 border-purple-500 pl-4">
                              <h4 className="text-purple-400 font-medium mb-2">{month}</h4>
                              {Array.isArray(actions) ? (
                                <ul className="space-y-1">
                                  {actions.map((action, i) => (
                                    <li key={`item-${i}`} className="text-gray-300 flex items-start gap-2">
                                      <ArrowRight className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
                                      {typeof action === 'string' ? action : (action.action || action.step || action.description || JSON.stringify(action))}
                                    </li>
                                  ))}
                                </ul>
                              ) : typeof actions === 'string' ? (
                                <p className="text-gray-300">{actions}</p>
                              ) : (
                                <p className="text-gray-300">{JSON.stringify(actions)}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Positive Outlook */}
                {aiAnalysis.ai_analysis?.positive_outlook && (
                  <Card className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 border-white/20 backdrop-blur-sm">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <CheckCircle className="w-8 h-8 text-green-400 flex-shrink-0" />
                        <div>
                          <h4 className="text-white font-medium mb-2">Positive Outlook</h4>
                          <p className="text-gray-200">{aiAnalysis.ai_analysis.positive_outlook}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Disclaimer */}
                <div className="text-center text-xs text-gray-500 p-4 bg-white/5 rounded-lg">
                  {aiAnalysis.disclaimer}
                </div>
              </div>
            )}
          </TabsContent>

          {/* Profile Tab */}
          <TabsContent value="profile" className="space-y-6">
            <Card className="bg-white/10 border-white/20 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Activity className="w-5 h-5 text-purple-400" />
                  Your Health Profile
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Update your profile for more accurate predictions
                </CardDescription>
              </CardHeader>
              <CardContent>
                <HealthProfileForm 
                  profile={profile} 
                  onSave={updateProfile}
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history" className="space-y-6">
            <PredictionHistory />
          </TabsContent>
        </Tabs>
      </div>

      {/* Prediction Detail Dialog */}
      <Dialog open={!!selectedPrediction} onOpenChange={() => setSelectedPrediction(null)}>
        <DialogContent className="bg-slate-900 border-white/20 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedPrediction && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {(() => {
                    const Icon = RISK_ICONS[selectedPrediction.category] || Activity;
                    return <Icon className="w-6 h-6 text-purple-400" />;
                  })()}
                  {selectedPrediction.name}
                </DialogTitle>
                <DialogDescription className="text-gray-400">
                  {selectedPrediction.description}
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-6 mt-4">
                {/* Risk Level */}
                <div className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                  <div>
                    <p className="text-gray-400 text-sm">Current Risk</p>
                    <p className="text-3xl font-bold text-white">{selectedPrediction.current_risk}%</p>
                  </div>
                  <Badge className={`${RISK_COLORS[selectedPrediction.risk_level]} text-lg px-4 py-2`}>
                    {selectedPrediction.risk_level.toUpperCase()}
                  </Badge>
                </div>

                {/* Factors */}
                {selectedPrediction.factors_analysis?.length > 0 && (
                  <div>
                    <h4 className="text-white font-medium mb-3">Risk Factors</h4>
                    <div className="space-y-2">
                      {selectedPrediction.factors_analysis.map((factor, idx) => (
                        <div key={`item-${idx}`} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                          <span className="text-gray-300">{factor.factor}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-gray-400 text-sm">{factor.value}</span>
                            <Badge className={`${factor.impact === 'positive' ? 'bg-green-500/20 text-green-400' : factor.impact === 'high' || factor.impact === 'critical' ? 'bg-red-500/20 text-red-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                              {factor.impact}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recommendations */}
                {selectedPrediction.recommendations?.length > 0 && (
                  <div>
                    <h4 className="text-white font-medium mb-3">Recommendations</h4>
                    <div className="space-y-2">
                      {selectedPrediction.recommendations.map((rec, idx) => (
                        <div key={`item-${idx}`} className="p-3 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-lg border border-purple-500/20">
                          <p className="text-white font-medium">{rec.action}</p>
                          <p className="text-green-400 text-sm mt-1">{rec.impact}</p>
                          <Badge className="mt-2" variant="outline">
                            Difficulty: {rec.difficulty}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* 6-Month Timeline */}
                {selectedPrediction.timeline?.length > 0 && (
                  <div>
                    <h4 className="text-white font-medium mb-3">6-Month Projection</h4>
                    <div className="relative">
                      <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-purple-500/30"></div>
                      <div className="space-y-4">
                        {selectedPrediction.timeline.map((point, idx) => (
                          <div key={`item-${idx}`} className="flex items-center gap-4 pl-8 relative">
                            <div className={`absolute left-2 w-4 h-4 rounded-full ${RISK_COLORS[point.risk_level].split(' ')[0]} border-2 border-slate-900`}></div>
                            <div className="flex-1 p-3 bg-white/5 rounded-lg">
                              <div className="flex justify-between items-center">
                                <span className="text-gray-400">{point.date}</span>
                                <span className={`font-medium ${RISK_COLORS[point.risk_level].split(' ')[1]}`}>
                                  {point.predicted_risk}%
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Potential Improvement */}
                {selectedPrediction.potential_improvement && (
                  <div className="p-4 bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-lg border border-green-500/30">
                    <h4 className="text-green-400 font-medium mb-2 flex items-center gap-2">
                      <TrendingDown className="w-4 h-4" />
                      Achievable Improvement
                    </h4>
                    <p className="text-white">
                      By following recommendations, you could reduce your risk from{' '}
                      <span className="text-red-400 font-bold">{selectedPrediction.potential_improvement.current_risk}%</span>
                      {' '}to{' '}
                      <span className="text-green-400 font-bold">{selectedPrediction.potential_improvement.achievable_risk}%</span>
                    </p>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Upgrade Dialog */}
      <Dialog open={showUpgradeDialog} onOpenChange={setShowUpgradeDialog}>
        <DialogContent className="bg-slate-900 border-white/20 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Crown className="w-6 h-6 text-yellow-400" />
              Upgrade Your Plan
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Get unlimited predictions and AI-powered insights
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {['basic', 'premium', 'family'].map((tier) => {
              const TierIcon = SUBSCRIPTION_TIERS[tier].icon;
              const prices = { basic: 199, premium: 499, family: 999 };
              const features = {
                basic: ['5 predictions/month', 'AI recommendations', 'Email alerts'],
                premium: ['Unlimited predictions', 'AI insights', 'What-if scenarios', 'Priority support'],
                family: ['Everything in Premium', 'Up to 5 family members', 'Family health insights']
              };
              
              return (
                <div 
                  key={tier}
                  className={`p-4 rounded-lg border cursor-pointer transition-all hover:scale-[1.02] ${
                    tier === 'premium' 
                      ? 'bg-gradient-to-r from-purple-500/20 to-pink-500/20 border-purple-500/50' 
                      : 'bg-white/5 border-white/20 hover:border-white/40'
                  }`}
                  onClick={() => upgradeSubscription(tier)}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <TierIcon className={`w-5 h-5 ${SUBSCRIPTION_TIERS[tier].color.replace('bg-', 'text-')}`} />
                      <span className="font-medium text-white capitalize">{tier}</span>
                      {tier === 'premium' && <Badge className="bg-yellow-500/20 text-yellow-400">Popular</Badge>}
                    </div>
                    <span className="text-xl font-bold text-white">₹{prices[tier]}<span className="text-sm text-gray-400">/mo</span></span>
                  </div>
                  <ul className="space-y-1">
                    {features[tier].map((feature, idx) => (
                      <li key={`item-${idx}`} className="text-sm text-gray-300 flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-400" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
          
          <p className="text-xs text-gray-500 text-center mt-4">
            Demo mode: No real payment required
          </p>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Health Profile Form Component
const HealthProfileForm = ({ profile, onSave }) => {
  const [formData, setFormData] = useState(profile || {});
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(formData);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div>
          <Label className="text-gray-400">Age</Label>
          <Input 
            type="number" 
            value={formData.age || ''} 
            onChange={(e) => setFormData({...formData, age: parseInt(e.target.value)})}
            className="bg-white/10 border-white/20 text-white mt-1"
          />
        </div>
        <div>
          <Label className="text-gray-400">Gender</Label>
          <select 
            value={formData.gender || ''} 
            onChange={(e) => setFormData({...formData, gender: e.target.value})}
            className="w-full bg-white/10 border border-white/20 text-white rounded-md px-3 py-2 mt-1"
          >
            <option value="">Select</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div>
          <Label className="text-gray-400">Height (cm)</Label>
          <Input 
            type="number" 
            value={formData.height_cm || ''} 
            onChange={(e) => setFormData({...formData, height_cm: parseFloat(e.target.value)})}
            className="bg-white/10 border-white/20 text-white mt-1"
          />
        </div>
        <div>
          <Label className="text-gray-400">Weight (kg)</Label>
          <Input 
            type="number" 
            value={formData.weight_kg || ''} 
            onChange={(e) => setFormData({...formData, weight_kg: parseFloat(e.target.value)})}
            className="bg-white/10 border-white/20 text-white mt-1"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <div>
          <Label className="text-gray-400">Blood Pressure (Systolic)</Label>
          <Input 
            type="number" 
            value={formData.blood_pressure_systolic || ''} 
            onChange={(e) => setFormData({...formData, blood_pressure_systolic: parseInt(e.target.value)})}
            className="bg-white/10 border-white/20 text-white mt-1"
            placeholder="e.g., 120"
          />
        </div>
        <div>
          <Label className="text-gray-400">Blood Pressure (Diastolic)</Label>
          <Input 
            type="number" 
            value={formData.blood_pressure_diastolic || ''} 
            onChange={(e) => setFormData({...formData, blood_pressure_diastolic: parseInt(e.target.value)})}
            className="bg-white/10 border-white/20 text-white mt-1"
            placeholder="e.g., 80"
          />
        </div>
        <div>
          <Label className="text-gray-400">Fasting Blood Sugar (mg/dL)</Label>
          <Input 
            type="number" 
            value={formData.blood_sugar_fasting || ''} 
            onChange={(e) => setFormData({...formData, blood_sugar_fasting: parseFloat(e.target.value)})}
            className="bg-white/10 border-white/20 text-white mt-1"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-gray-400">Exercise (min/week): {formData.exercise_minutes_weekly || 150}</Label>
          <Slider
            value={[formData.exercise_minutes_weekly || 150]}
            onValueChange={(value) => setFormData({...formData, exercise_minutes_weekly: value[0]})}
            max={500}
            step={10}
            className="mt-2"
          />
        </div>
        <div>
          <Label className="text-gray-400">Sleep (hours/night): {formData.sleep_hours_avg || 7}</Label>
          <Slider
            value={[formData.sleep_hours_avg || 7]}
            onValueChange={(value) => setFormData({...formData, sleep_hours_avg: value[0]})}
            max={12}
            min={3}
            step={0.5}
            className="mt-2"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-gray-400">Stress Level: {formData.stress_level || 5}/10</Label>
          <Slider
            value={[formData.stress_level || 5]}
            onValueChange={(value) => setFormData({...formData, stress_level: value[0]})}
            max={10}
            min={1}
            step={1}
            className="mt-2"
          />
        </div>
        <div>
          <Label className="text-gray-400">Diet Quality: {formData.diet_quality || 6}/10</Label>
          <Slider
            value={[formData.diet_quality || 6]}
            onValueChange={(value) => setFormData({...formData, diet_quality: value[0]})}
            max={10}
            min={1}
            step={1}
            className="mt-2"
          />
        </div>
      </div>

      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <Switch
            checked={formData.smoker || false}
            onCheckedChange={(checked) => setFormData({...formData, smoker: checked})}
          />
          <Label className="text-gray-400">Smoker</Label>
        </div>
        <div className="flex-1">
          <Label className="text-gray-400">Alcohol (drinks/week)</Label>
          <Input 
            type="number" 
            value={formData.alcohol_weekly || ''} 
            onChange={(e) => setFormData({...formData, alcohol_weekly: parseInt(e.target.value)})}
            className="bg-white/10 border-white/20 text-white mt-1"
          />
        </div>
      </div>

      <Button 
        onClick={handleSave}
        disabled={saving}
        className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
        data-testid="save-profile-btn"
      >
        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
        Save Profile
      </Button>
    </div>
  );
};

// Prediction History Component
const PredictionHistory = () => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadHistory = async () => {
    try {
      const response = await axios.get(`${API}/health-predictions/history`);
      setHistory(response.data.history || []);
    } catch (error) {
      console.error('Error loading history:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <Card className="bg-white/10 border-white/20 backdrop-blur-sm">
        <CardContent className="p-8 text-center">
          <History className="w-12 h-12 text-gray-500 mx-auto mb-4" />
          <p className="text-gray-400">No prediction history yet</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {history.map((record, idx) => (
        <Card key={`item-${idx}`} className="bg-white/10 border-white/20 backdrop-blur-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white font-medium">
                  Prediction #{history.length - idx}
                </p>
                <p className="text-gray-400 text-sm">
                  {new Date(record.generated_at).toLocaleString()}
                </p>
              </div>
              <div className="text-right">
                <p className="text-gray-400 text-sm">High Risk Areas</p>
                <p className="text-orange-400 font-bold">{record.high_risk_count || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default PremiumHealthPredictions;
